"use client"

import { useEffect, useState } from "react"

import { cn } from "@workspace/ui/lib/utils"

import { Link, usePathname } from "@/i18n/navigation"

export interface ShowcaseNavLink {
  href: string
  label: string
}

/**
 * The `showcase` nav band's centred links, marking the current one
 * (`aria-current="page"`, full-strength text) the way the reference sites set
 * their selected nav item apart from the dimmer rest.
 *
 * A link is current when the path matches — `/` exactly, anything else as a
 * prefix (`/menu` stays lit on category and item pages). Links into a section
 * of a page (`/info#opening-times`) also need the hash to match, so the
 * section link doesn't light up for the whole page; the hash is tracked
 * client-side, as it never reaches the server.
 *
 * Client-side because the header lives in the (store) layout, which Next.js
 * reuses across navigations: a server-rendered "current" would stick to the
 * first page opened.
 */
export function ShowcaseNavLinks({
  links,
  className,
}: {
  links: ShowcaseNavLink[]
  className?: string
}) {
  const pathname = usePathname()
  const [hash, setHash] = useState("")

  useEffect(() => {
    const sync = () => setHash(window.location.hash)
    sync()
    window.addEventListener("hashchange", sync)
    return () => window.removeEventListener("hashchange", sync)
  }, [pathname])

  const isCurrent = (href: string) => {
    const [path = "/", fragment] = href.split("#")
    const onPath =
      path === "/"
        ? pathname === "/"
        : pathname === path || pathname.startsWith(`${path}/`)
    return fragment === undefined ? onPath : onPath && hash === `#${fragment}`
  }

  return (
    <ul className={cn("flex items-center gap-x-[26px]", className)}>
      {links.map((link) => {
        const current = isCurrent(link.href)
        const fragment = link.href.split("#")[1]
        return (
          <li key={link.href}>
            <Link
              href={link.href}
              aria-current={current ? "page" : undefined}
              // Same-page hash links don't fire `hashchange` under the App
              // Router (it pushes history state), so record the hash here.
              onClick={fragment ? () => setHash(`#${fragment}`) : undefined}
              className={
                current
                  ? "inline-flex items-center font-display text-[12pt] leading-[39px] whitespace-nowrap text-foreground"
                  : "inline-flex items-center font-display text-[12pt] leading-[39px] whitespace-nowrap text-foreground/70 underline-offset-4 hover:text-foreground hover:underline"
              }
            >
              {link.label}
            </Link>
          </li>
        )
      })}
    </ul>
  )
}
